import type { BillingQuestion } from "@/features/timesheetFlow/timesheetFlowApi";

type LegacyBillingQuestionRow = {
  id?: number;
  billing_question_id?: number;
  item_id?: number | null;
  question?: string | null;
  question_text?: string | null;
  answer?: string | null;
  response_text?: string | null;
  material_id?: number | null;
  material?: {
    id?: number;
    text?: string | null;
    menge?: string | number | null;
    einheit?: string | null;
  } | null;
  responded_at?: string | null;
  price?: string | number | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function looksLikeBillingQuestion(row: Record<string, unknown>): boolean {
  if (Array.isArray(row.items)) return true;
  if (row.timesheet_id != null || row.created_by_login != null || row.assigned_to_group != null) {
    return true;
  }
  if (row.question_text != null && row.question == null && row.item_id == null && row.material_id == null) {
    return true;
  }
  return false;
}

function extractBillingQuestionsArray(data: unknown): unknown[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (!isRecord(data)) return [];

  if (looksLikeBillingQuestion(data)) return [data];

  if (Array.isArray(data.questions)) return data.questions;
  if (Array.isArray(data.billing_questions)) return data.billing_questions;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) {
    const items = data.items;
    const first = items[0];
    if (isRecord(first) && looksLikeBillingQuestion(first)) return items;
  }
  return [];
}

function isCanonicalBillingQuestion(row: Record<string, unknown>): boolean {
  if (Array.isArray(row.items)) return true;
  if (looksLikeBillingQuestion(row) && row.item_id == null && row.material_id == null) {
    return true;
  }
  return false;
}

/** API-Antwort (kanonisch oder Legacy-Flat-Rows) in BillingQuestion[] überführen. */
export function normalizeBillingQuestionsFromApi(data: unknown): BillingQuestion[] {
  const raw = extractBillingQuestionsArray(data);
  if (!raw.length) return [];

  const first = raw[0];
  if (typeof first !== "object" || first === null) return [];

  if (isCanonicalBillingQuestion(first as Record<string, unknown>)) {
    return raw.filter((row) => isRecord(row) && looksLikeBillingQuestion(row)) as BillingQuestion[];
  }

  const byId = new Map<number, BillingQuestion>();

  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as LegacyBillingQuestionRow;
    const qid = Number(row.billing_question_id ?? row.id);
    if (!Number.isFinite(qid)) continue;

    let question = byId.get(qid);
    if (!question) {
      question = {
        id: qid,
        question_text: null,
        response_text: null,
        items: [],
        responded_at: row.responded_at ?? null,
      };
      byId.set(qid, question);
    }

    const text = String(row.question ?? row.question_text ?? "").trim();
    const materialId = row.material_id ?? row.material?.id ?? null;
    const itemId = row.item_id;

    if (materialId != null || itemId != null) {
      const items = question.items ?? [];
      items.push({
        id: Number(itemId) || items.length + 1,
        material_position_id: materialId != null ? Number(materialId) : null,
        question_text: text,
        response_text: String(row.answer ?? row.response_text ?? "").trim() || undefined,
        price: row.price ?? null,
        material_text: row.material?.text ?? undefined,
        material_menge: row.material?.menge ?? undefined,
        material_einheit: row.material?.einheit ?? undefined,
      });
      question.items = items;
    } else if (text) {
      question.question_text = text;
      const answer = String(row.answer ?? row.response_text ?? "").trim();
      if (answer) question.response_text = answer;
    }
  }

  return Array.from(byId.values());
}
